import { Cookies } from "react-cookie";
import { ABTestName, ABTestVariant } from "./abTestingTypes";

const AB_TESTS_COOKIE_NAME = "ab_tests";

type ABTestsData = Record<string, string>;

export class ABTestingService {
  private readonly _cookies = new Cookies(null);

  /**
   * Gets the variant for a specific AB test
   * @param testName - The name of the AB test
   * @returns The variant string (e.g., "treatment", "control") or null if not found
   */
  getVariant(testName: ABTestName): string | null {
    const abTests = this._getABTests();
    return abTests[testName] ?? this._cookies.get<string>(testName) ?? null;
  }

  /**
   * Checks if a specific AB test has the treatment variant
   * @param testName - The name of the AB test
   * @returns true if the test has the treatment variant, false otherwise
   */
  isTreatment(testName: ABTestName): boolean {
    return this.getVariant(testName) === ABTestVariant.Treatment;
  }

  /**
   * Checks if a specific AB test has the control variant
   * @param testName - The name of the AB test
   * @returns true if the test has the control variant, false otherwise
   */
  isControl(testName: ABTestName): boolean {
    return this.getVariant(testName) === ABTestVariant.Control;
  }

  /**
   * Gets all AB tests data from the cookie
   * @returns Object containing all AB test names and their variants
   */
  private _getABTests(): ABTestsData {
    try {
      const cookieValue = this._cookies.get<ABTestsData>(AB_TESTS_COOKIE_NAME);

      if (!cookieValue) {
        return {};
      }

      return cookieValue;
    } catch (error) {
      console.error("Failed to read AB tests cookie:", error);
      return {};
    }
  }

  /**
   * Gets all AB tests for debugging purposes
   * @returns Object containing all AB test names and their variants
   */
  getAllTests(): ABTestsData {
    return this._getABTests();
  }
}
